import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useAppSelector } from 'core/store/hooks';
import { RptAgendaCommercialeDto } from '@api/client';
import { agendaActionsSelector, agendaActionsPagingSelector } from '../_redux/selectors';
import { opportunitiesSelector } from '../../agenda-opportunities/_redux/selectors';

export interface MappedFieldAction {
  label: string;
  value: string | number | boolean | undefined;
  visible: boolean;
  className?: string;
  esitoVariant?: string;
  isDoneIcon?: boolean;
  isStatoBadge?: boolean;
  isActionsDropdown?: boolean;
  isLink?: boolean;
  oppSfLink?: string;
  truncate?: boolean;
}

export interface MappedAgendaAction {
  id: MappedFieldAction;
  status: MappedFieldAction;
  fkCliente: MappedFieldAction;
  nominativoCliente: MappedFieldAction;
  actionDate: MappedFieldAction;
  modalitaContatto: MappedFieldAction;
  esito: MappedFieldAction;
  opportunity: MappedFieldAction;
  nota: MappedFieldAction;
  lastmodifiedDate: MappedFieldAction;
  azione: MappedFieldAction;
}

const ESITO_VARIANT: Record<string, string> = {
  POSITIVO: 'success',
  NEGATIVO: 'danger',
  NEUTRO: 'warning',
  ANNULLATA: 'secondary',
};

const useGetAndMapAgendaActions = () => {
  const { t } = useTranslation();
  const [actions, setActions] = useState<MappedAgendaAction[]>([]);

  const rawActions = useAppSelector(agendaActionsSelector);
  const paging = useAppSelector(agendaActionsPagingSelector);
  const opportunities = useAppSelector(opportunitiesSelector);

  useEffect(() => {
    if (rawActions?.length) {
      setActions(mapActions(rawActions));
    } else {
      setActions([]);
    }
  }, [rawActions, opportunities]);

  const mapActions = (items: RptAgendaCommercialeDto[]): MappedAgendaAction[] => {
    return items.map((item) => ({
      id: { label: 'ID', value: item.id, visible: false },
      status: {
        label: t('Stato'),
        value: item.statoAttivita || '-',
        visible: true,
        isStatoBadge: true,
        className: 'text-center',
      },
      fkCliente: { label: t('Cliente'), value: item.nomeAccount || item.clienteId || '-', visible: true, isLink: true },
      nominativoCliente: { label: t('Referente'), value: item.nominativoCliente || '-', visible: true },
      actionDate: {
        label: t('Data azione'),
        value: item.actionDate ? format(new Date(item.actionDate), 'dd/MM/yyyy') : '-',
        visible: true,
      },
      modalitaContatto: {
        label: t('Modalità contatto'),
        value: item.modalitaContatto || '-',
        visible: true,
      },
      esito: {
        label: t('Esito'),
        value: item.esito || '-',
        visible: true,
        esitoVariant: item.esito ? ESITO_VARIANT[item.esito] : undefined,
      },
      opportunity: {
        label: t('Opportunity'),
        value: item.denominazioneOpportunity || '',
        visible: true,
        oppSfLink: item.opportunityId
          ? opportunities.find((o) => o.pkOpportunity === item.opportunityId)?.linkOppSF
          : undefined,
      },
      nota: { label: t('Nota'), value: item.nota || '-', visible: true, truncate: true },
      lastmodifiedDate: {
        label: t('Ultima modifica'),
        value: item.lastmodifiedDate ? format(new Date(item.lastmodifiedDate), 'dd/MM/yyyy') : '-',
        visible: true,
      },
      azione: {
        label: '',
        value: '',
        visible: true,
        isActionsDropdown: true,
        className: 'text-center',
      },
    }));
  };

  return { actions, rawActions, paging };
};

export default useGetAndMapAgendaActions;
